import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Location } from '@angular/common';
import { SystemService } from '../../services/system.service';
import { GlobalSearchService } from '../../services/globalsearchservice.service';
import { UsersService } from '../../services/users.service';
import { GlobalsService } from '../../services/globals.service';

@Component({
	selector: 'app-system-notice',
	templateUrl: './system-notice.component.html',
	styleUrls: ['./system-notice.component.scss']
})
export class SystemNoticeComponent implements OnInit {
	noticeForm: UntypedFormGroup;
	color = 'blue';
	userGroups: any[] = [];
	locations: any[] = [];
	notices: any[] = [];

	editingNotice: any = null;

	constructor(private fb: UntypedFormBuilder,
				private systemService: SystemService,
				private globalSearchService: GlobalSearchService,
				private usersService: UsersService,
				private globalsService: GlobalsService,
				private location: Location) {
		this.color = this.globalSearchService.getColor();
		this.noticeForm = this.fb.group({
			message: ['', Validators.required],
			priority: [0, Validators.required],
			groups: [[]],
			locations: [[]]
		});
	}

	ngOnInit(): void {
		this.loadLists();
		this.loadNotices();
	}

	loadLists() {
		this.usersService.getUserGroups().subscribe({
			next: (groups: any) => {
				this.userGroups = groups || [];
			},
			error: (error: any) => {
				console.error('Error loading user groups:', error);
				this.globalSearchService.showNotification('Failed to load user groups', 'danger', 'bottom', 'right');
				this.userGroups = [];
			}
		});

		this.globalsService.getAllLocations().subscribe({
			next: (locs: any) => {
				this.locations = locs || [];
			},
			error: (error: any) => {
				console.error('Error loading locations:', error);
				this.globalSearchService.showNotification('Failed to load locations', 'danger', 'bottom', 'right');
				this.locations = [];
			}
		});
	}

	/**
	 * Safely parse JSON data that might be a string or already an array/object
	 * Handles MySQL JSON columns that may return arrays directly
	 */
	private safeJsonParse(value: any, defaultValue: any = []): any {
		if (value === null || value === undefined || value === '') {
			return defaultValue;
		}
		
		// If already an array or object, return as-is
		if (Array.isArray(value) || (typeof value === 'object' && value !== null)) {
			return value;
		}
		
		// If it's a string, try to parse it
		if (typeof value === 'string') {
			try {
				return JSON.parse(value);
			} catch (e) {
				console.warn('Failed to parse JSON:', value, e);
				return defaultValue;
			}
		}
		
		return defaultValue;
	}

	loadNotices() {
		this.systemService.getSystemNotice().subscribe({
			next: (result: any[]) => {
				try {
					if (Array.isArray(result)) {
						this.notices = result.map(n => ({
							...n,
							groups: this.safeJsonParse(n.groups, []),
							locations: this.safeJsonParse(n.locations, [])
						}));
					} else {
						this.notices = [];
					}
				} catch (error) {
					console.error('Error processing notices:', error);
					this.globalSearchService.showNotification('Error loading notices', 'danger', 'bottom', 'right');
					this.notices = [];
				}
			},
			error: (error: any) => {
				console.error('Error loading notices:', error);
				this.globalSearchService.showNotification('Failed to load system notices', 'danger', 'bottom', 'right');
				this.notices = [];
			}
		});
	}


	saveNotice() {
		if (this.noticeForm.invalid) {
			this.globalSearchService.showNotification('Please fill in all required fields', 'warning', 'bottom', 'right');
			return;
		}

		const data = { ...this.noticeForm.value };

		if (this.editingNotice) {
			// Update
			data.id = this.editingNotice.id;
			this.systemService.updateSystemNotice(data).subscribe({
				next: () => {
					this.globalSearchService.showNotification('Notice Updated', 'info', 'bottom', 'right');
					this.resetForm();
					this.loadNotices();
				},
				error: (error: any) => {
					console.error('Error updating notice:', error);
					this.globalSearchService.showNotification('Failed to update notice', 'danger', 'bottom', 'right');
				}
			});
		} else {
			// Create
			this.systemService.saveSystemNotice(data).subscribe({
				next: () => {
					this.globalSearchService.showNotification('Notice Saved', 'success', 'bottom', 'right');
					this.resetForm();
					this.loadNotices();
				},
				error: (error: any) => {
					console.error('Error saving notice:', error);
					this.globalSearchService.showNotification('Failed to save notice', 'danger', 'bottom', 'right');
				}
			});
		}
	}

	editNotice(n: any) {
		this.editingNotice = n;
		this.noticeForm.patchValue({
			message: n.message,
			priority: n.priority || 0,
			groups: n.groups || [],
			locations: n.locations || []
		});
	}

	deleteNotice(n: any) {
		if (!confirm('Delete this notice?')) {return;}

		const data = {
			id: n.id
		}

		this.systemService.deleteSystemNotice(data).subscribe({
			next: () => {
				this.globalSearchService.showNotification('Notice Deleted', 'warning', 'bottom', 'right');
				this.loadNotices();
				this.resetForm();
			},
			error: (error: any) => {
				console.error('Error deleting notice:', error);
				this.globalSearchService.showNotification('Failed to delete notice', 'danger', 'bottom', 'right');
			}
		});
	}

	cancelEdit() {
		this.resetForm();
	}

	resetForm() {
		this.editingNotice = null;
		this.noticeForm.reset({ message: '', priority: 0, groups: [], locations: [] });
	}

	back(): void {
		this.location.back();
	}
}